import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import FollowUpStudentsTable from '@/tables/FollowUpStudentsTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-students/')({
  component: FollowUpStudentsScreen,
});

/**
 * The All Students tab for both follow-up roles: the caller's own caseload,
 * with the endpoint branching on their role.
 *
 * No admin: the endpoint's requirement turns the admin bypass off, because an
 * admin is neither a coordinator nor a partner and would only ever get an empty
 * list. There's no admin nav item either.
 */
function FollowUpStudentsScreen(): ReactElement | null {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, not `useCurrentUser().isStudyHelper`, which includes the
  // study helpers and admins the endpoint refuses. Defence in depth;
  // `isPending` first, or the coordinator bounces themselves while `/users/me`
  // loads.
  const canView =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' ||
    me?.profile_type === 'FOLLOWUP_PARTNER';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* Help placement by the caller's role, since the follow-up group
           types this used to key on are gone. The last arm can't be
           reached behind the guard, but is written out so a default
           doesn't render a help icon legacy doesn't. */}
        <TitleWithQuestion
          title={t('lessons.all-students')}
          placement={
            me?.profile_type === 'FOLLOWUP_COORDINATOR'
              ? 'follow_up_coordinator_students'
              : me?.profile_type === 'FOLLOWUP_PARTNER'
                ? 'follow_up_partner_students'
                : undefined
          }
        />
      </GroupHeaderWrapper>
      <FollowUpStudentsTable />
    </PageContainer>
  );
}
